import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How long the page stays on a section before moving on: a fixed dwell,
 * or until the WEE story has finished Enact (with a ceiling, in case it
 * never reports).
 */
export type AutoplayStep = { dwellMs: number } | { untilStoryEnds: true; maxMs: number };

/** The scroll between sections: slow, eased at both ends. */
const SCROLL_MS = 1600;

/** Keys that scroll the page: pressing one means the reader has taken over. */
const SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'PageDown',
  'PageUp',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

function easeInOutCubic(p: number): number {
  return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
}

type Autoplay = {
  playing: boolean;
  /** The section the autoplay is on — the one it last arrived at. */
  index: number;
  toggle: () => void;
  pause: () => void;
  /** A section's story finished a telling; the page moves on only if it's that section's turn. */
  storyEnded: (section: number) => void;
  /** The reader moved a section's story (How we build's acts): its telling starts over from there. */
  storySteered: (section: number) => void;
};

/**
 * The page's autoplay rhythm: dwell on a section, glide to the next, and
 * so on through its steps; arriving at a section past the last step, it
 * stops (June, 2026-09-23: it rests at Download). Any sign the reader has
 * taken over — a wheel, a touch, a scrolling key, a click on the page —
 * pauses it; the control plays it again from wherever they are. Steering a
 * story (an element inside `[data-autoplay-steer]`, How we build's acts)
 * is not taking over: the story keeps the page's turn and moves it on when
 * it ends. It starts paused when `startPlaying` is false (reduced motion,
 * the screenshot suite).
 */
export function useAutoplay(
  sections: React.MutableRefObject<(HTMLElement | null)[]>,
  steps: readonly AutoplayStep[],
  startPlaying: boolean,
  instant: boolean,
): Autoplay {
  const [playing, setPlaying] = useState(startPlaying);
  const [index, setIndex] = useState(0);
  const playingRef = useRef(playing);
  const indexRef = useRef(0);
  const timer = useRef<number | null>(null);
  const scrollRaf = useRef(0);
  const waitingForStory = useRef(false);
  /** When the current dwell ends, for suspending it while the tab is hidden. */
  const due = useRef<{ at: number; left: number } | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    due.current = null;
  };

  const stopScroll = () => {
    cancelAnimationFrame(scrollRaf.current);
    scrollRaf.current = 0;
  };

  const pause = useCallback(() => {
    playingRef.current = false;
    waitingForStory.current = false;
    clearTimer();
    stopScroll();
    setPlaying(false);
  }, []);

  // Declared before `arrive` and `advance` use them; filled in below so the
  // three can call one another.
  const advanceRef = useRef<(to: number) => void>(() => undefined);

  const wait = (ms: number, then: () => void) => {
    clearTimer();
    due.current = { at: performance.now() + ms, left: ms };
    timer.current = window.setTimeout(() => {
      timer.current = null;
      due.current = null;
      then();
    }, ms);
  };

  const arrive = (i: number) => {
    indexRef.current = i;
    setIndex(i);
    const step = steps[i];
    if (!step) {
      // Past the last step: the rhythm is done, and the page rests here.
      pause();
      return;
    }
    if ('untilStoryEnds' in step) {
      waitingForStory.current = true;
      wait(step.maxMs, () => advanceRef.current(i + 1));
    } else {
      waitingForStory.current = false;
      wait(step.dwellMs, () => advanceRef.current(i + 1));
    }
  };

  advanceRef.current = (to: number) => {
    if (!playingRef.current) return;
    const target = sections.current[to];
    if (!target) {
      // The foot of the page: the rhythm is done.
      pause();
      return;
    }
    waitingForStory.current = false;
    clearTimer();
    stopScroll();
    const from = window.scrollY;
    const toY = target.getBoundingClientRect().top + window.scrollY;
    if (instant) {
      window.scrollTo(0, toY);
      arrive(to);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / SCROLL_MS);
      window.scrollTo(0, Math.round(from + (toY - from) * easeInOutCubic(p)));
      if (p < 1) {
        scrollRaf.current = requestAnimationFrame(step);
      } else {
        scrollRaf.current = 0;
        arrive(to);
      }
    };
    scrollRaf.current = requestAnimationFrame(step);
  };

  /** The section nearest the top of the screen. */
  const sectionInView = () => {
    const line = window.innerHeight * 0.4;
    let found = 0;
    sections.current.forEach((el, i) => {
      if (el && el.getBoundingClientRect().top <= line) found = i;
    });
    return found;
  };

  const play = useCallback(() => {
    playingRef.current = true;
    setPlaying(true);
    const here = sectionInView();
    const last = sections.current.length - 1;
    // At the foot of the page, play means from the top.
    if (here >= last) advanceRef.current(0);
    else arrive(here);
    // `arrive` and `sectionInView` read only refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(() => {
    if (playingRef.current) pause();
    else play();
  }, [pause, play]);

  // Two sections tell stories; only the one being shown may move the page on.
  const storyEnded = useCallback((section: number) => {
    if (playingRef.current && waitingForStory.current && section === indexRef.current) {
      advanceRef.current(indexRef.current + 1);
    }
  }, []);

  // The reader moved the story being shown: the ceiling starts again, so
  // the telling they chose finishes before the page moves on.
  const storySteered = useCallback((section: number) => {
    const step = steps[section];
    if (playingRef.current && waitingForStory.current && section === indexRef.current && step && 'untilStoryEnds' in step) {
      wait(step.maxMs, () => advanceRef.current(section + 1));
    }
    // `steps` is the page's constant rhythm; `wait` reads only refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start: the first section's dwell, if the page opens playing.
  useEffect(() => {
    if (playingRef.current) arrive(0);
    return () => {
      clearTimer();
      stopScroll();
    };
    // Runs once; everything it touches is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The reader takes over: pause.
  useEffect(() => {
    const takeOver = () => {
      if (playingRef.current) pause();
    };
    // The control itself is not the reader taking over: it toggles. Nor is
    // steering a story: pressing one of How we build's acts once paused the
    // whole page, so the story's end never moved it on (June, 2026-09-23).
    const outsideControl = (e: Event) => {
      if ((e.target as Element | null)?.closest?.('[data-autoplay-control], [data-autoplay-steer]')) return;
      takeOver();
    };
    const onKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) outsideControl(e);
    };
    window.addEventListener('wheel', takeOver, { passive: true });
    window.addEventListener('touchstart', outsideControl, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', outsideControl);
    return () => {
      window.removeEventListener('wheel', takeOver);
      window.removeEventListener('touchstart', outsideControl);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', outsideControl);
    };
  }, [pause]);

  // A hidden tab holds its place: the dwell stops counting and resumes.
  useEffect(() => {
    const onVisibility = () => {
      if (!playingRef.current) return;
      if (document.hidden) {
        const d = due.current;
        if (d && timer.current !== null) {
          const left = Math.max(0, d.at - performance.now());
          window.clearTimeout(timer.current);
          timer.current = null;
          due.current = { at: 0, left };
        }
      } else if (due.current && timer.current === null) {
        const i = indexRef.current;
        wait(due.current.left, () => advanceRef.current(i + 1));
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { playing, index, toggle, pause, storyEnded, storySteered };
}
